import React, { useEffect, useState } from "react";
import { Link, Route, Routes, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Users, FileText, Clock, CheckCircle2, Plus, MessageCircle, ShieldCheck,
  Receipt, ClipboardList, Send, AlertCircle, Download, Loader2,
} from "lucide-react";
import BackButton from "@/components/business/BackButton";
import MetricCard from "@/components/business/MetricCard";
import PageHeader from "@/components/business/PageHeader";
import StatusBanner from "@/components/business/StatusBanner";
import { inr } from "@/lib/format";

const STATUS_BADGES = {
  draft: { label: "Draft", cls: "bg-amber-500/10 text-amber-700" },
  prepared: { label: "Prepared", cls: "bg-blue-500/10 text-blue-700" },
  awaiting_user_submission: { label: "Self-filing", cls: "bg-blue-500/10 text-blue-700" },
  ca_requested: { label: "New request", cls: "bg-rose-500/10 text-rose-700 font-semibold" },
  ca_in_review: { label: "In review", cls: "bg-violet-500/10 text-violet-700" },
  ca_docs_pending: { label: "Docs pending", cls: "bg-amber-500/10 text-amber-700" },
  ca_filing: { label: "Filing on portal", cls: "bg-violet-500/10 text-violet-700" },
  filed_with_government: { label: "Filed with ITD", cls: "bg-emerald-500/10 text-emerald-700" },
};

export default function CAPortal() {
  return (
    <Routes>
      <Route index element={<CAHome />} />
      <Route path="clients" element={<CAClients />} />
      <Route path="filings" element={<CAFilings />} />
      <Route path="filings/:filingId" element={<CAFilingDetail />} />
    </Routes>
  );
}

function CAHome() {
  const [stats, setStats] = useState({});
  useEffect(() => { api.get("/ca/stats").then(({ data }) => setStats(data)); }, []);
  return (
    <AppShell>
      <div className="max-w-6xl mx-auto">
        <PageHeader>
          <PageHeader.Title>CA Workspace</PageHeader.Title>
        </PageHeader>
        <p className="mt-2 text-sm text-muted-foreground">Manage clients, review filings, and file at incometax.gov.in.</p>

        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard label="Clients" value={stats.clients || 0} icon={Users} testId="ca-stat-clients" />
          <MetricCard label="New requests" value={stats.new_requests || 0} icon={Clock} highlight={(stats.new_requests || 0) > 0} testId="ca-stat-requests" />
          <MetricCard label="In workflow" value={stats.in_workflow || 0} icon={ClipboardList} testId="ca-stat-workflow" />
          <MetricCard label="Filed with ITD" value={stats.filed_with_government || 0} icon={ShieldCheck} testId="ca-stat-filed" />
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Card className="p-6 border-border">
            <div className="font-display font-semibold text-lg">Filings queue</div>
            <p className="mt-2 text-sm text-muted-foreground">Review pending returns and proceed step-by-step until filed with the Income Tax Department.</p>
            <Button asChild className="mt-4"><Link to="/ca-portal/filings">Open queue →</Link></Button>
          </Card>
          <Card className="p-6 border-border">
            <div className="font-display font-semibold text-lg">Clients</div>
            <p className="mt-2 text-sm text-muted-foreground">All clients assigned to you.</p>
            <Button asChild className="mt-4"><Link to="/ca-portal/clients">Open clients →</Link></Button>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}


function CAClients() {
  const [clients, setClients] = useState([]);
  const [email, setEmail] = useState("");
  const load = () => api.get("/ca/clients").then(({ data }) => setClients(data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    try { await api.post("/ca/clients", { client_email: email }); toast.success("Client added"); setEmail(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <AppShell>
      <div className="max-w-5xl mx-auto">
        <BackButton to="/ca-portal" label="Workspace" />
        <PageHeader>
          <PageHeader.Title>Clients</PageHeader.Title>
        </PageHeader>
        <Card className="mt-6 p-5 border-border flex gap-2">
          <Input placeholder="Client's email address" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="add-client-email" />
          <Button onClick={add} data-testid="add-client-btn" className="h-11"><Plus className="h-4 w-4 mr-1" /> Add</Button>
        </Card>
        <Card className="mt-6 border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">PAN</th><th className="text-left p-3">Filings</th></tr>
            </thead>
            <tbody>
              {clients.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">No clients yet.</td></tr>}
              {clients.map((c) => (
                <tr key={c.id} className="border-t border-border">
                  <td className="p-3 font-medium">{c.full_name}</td>
                  <td className="p-3">{c.email}</td>
                  <td className="p-3 font-mono text-xs">{c.pan || "—"}</td>
                  <td className="p-3 num-rupee">{c.filings_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </AppShell>
  );
}

function CAFilings() {
  const [filings, setFilings] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const load = () => api.get(`/ca/filings${statusFilter ? `?status=${statusFilter}` : ""}`).then(({ data }) => setFilings(data));
  useEffect(() => { load(); }, [statusFilter]);

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto">
        <BackButton to="/ca-portal" label="Workspace" />
        <PageHeader>
          <PageHeader.Title>Filings queue</PageHeader.Title>
          <PageHeader.Actions>
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="h-10 w-56"><SelectValue placeholder="Filter by status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="ca_requested">New requests</SelectItem>
              <SelectItem value="ca_in_review">In review</SelectItem>
              <SelectItem value="ca_docs_pending">Docs pending</SelectItem>
              <SelectItem value="ca_filing">Filing on portal</SelectItem>
              <SelectItem value="filed_with_government">Filed with ITD</SelectItem>
            </SelectContent>
          </Select>
          </PageHeader.Actions>
        </PageHeader>

        <Card className="mt-6 border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              <tr>
                <th className="text-left p-3">Client</th>
                <th className="text-left p-3">Form</th>
                <th className="text-left p-3">AY</th>
                <th className="text-left p-3">Regime</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filings.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground text-sm">No filings.</td></tr>}
              {filings.map(f => {
                const b = STATUS_BADGES[f.status] || { label: f.status, cls: "bg-muted" };
                return (
                  <tr key={f.id} className="border-t border-border hover:bg-muted/30">
                    <td className="p-3">
                      <div className="font-medium">{f.client_name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{f.client_email}</div>
                    </td>
                    <td className="p-3 font-medium">{f.itr_type}</td>
                    <td className="p-3">{f.assessment_year}</td>
                    <td className="p-3 capitalize">{f.regime}</td>
                    <td className="p-3"><span className={`text-xs rounded px-2 py-0.5 ${b.cls}`}>{b.label}</span></td>
                    <td className="p-3 text-xs text-muted-foreground">{new Date(f.updated_at).toLocaleDateString()}</td>
                    <td className="p-3 text-right"><Button asChild size="sm" variant="outline"><Link to={`/ca-portal/filings/${f.id}`} data-testid={`open-${f.id.slice(0,6)}`}>Open</Link></Button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      </div>
    </AppShell>
  );
}

function CAFilingDetail() {
  const { filingId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [docReqs, setDocReqs] = useState([]);
  const [notes, setNotes] = useState([]);
  const [checklist, setChecklist] = useState(null);
  const [invoices, setInvoices] = useState([]);

  const [msgText, setMsgText] = useState("");
  const [noteText, setNoteText] = useState("");
  const [docName, setDocName] = useState("");
  const [docReason, setDocReason] = useState("");
  const [invAmount, setInvAmount] = useState(1299);
  const [invDesc, setInvDesc] = useState("CA-assisted Income Tax Return filing");
  const [ackNumber, setAckNumber] = useState("");
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [filedOn, setFiledOn] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [f, m, d, n, c, inv] = await Promise.all([
      api.get(`/ca/filings/${filingId}`),
      api.get(`/ca/filings/${filingId}/messages`),
      api.get(`/ca/filings/${filingId}/doc-requests`),
      api.get(`/ca/filings/${filingId}/notes`),
      api.get(`/ca/filings/${filingId}/checklist`),
      api.get(`/ca/filings/${filingId}/invoices`),
    ]);
    setData(f.data); setMessages(m.data); setDocReqs(d.data); setNotes(n.data); setChecklist(c.data); setInvoices(inv.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filingId]);

  if (!data) return <AppShell><div className="py-10"><Loader2 className="h-5 w-5 animate-spin" /></div></AppShell>;
  const { filing, client } = data;

  const setStatus = async (s) => {
    setBusy(true);
    try { await api.post(`/ca/filings/${filingId}/status`, { status: s }); toast.success("Status updated"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const sendMsg = async () => {
    if (!msgText.trim()) return;
    await api.post(`/ca/filings/${filingId}/messages`, { text: msgText });
    setMsgText(""); load();
  };

  const requestDoc = async () => {
    if (!docName.trim()) return;
    await api.post(`/ca/filings/${filingId}/doc-requests`, { doc_name: docName, reason: docReason });
    setDocName(""); setDocReason(""); toast.success("Document requested"); load();
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    await api.post(`/ca/filings/${filingId}/notes`, { text: noteText });
    setNoteText(""); load();
  };

  const createInvoice = async () => {
    await api.post(`/ca/filings/${filingId}/invoices`, { amount: Number(invAmount), description: invDesc });
    toast.success("Invoice issued"); load();
  };

  const finalize = async () => {
    if ((ackNumber || "").trim().length < 12) {
      toast.error("Please enter the real ITR-V acknowledgement number from incometax.gov.in (15 digits)");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/ca/filings/${filingId}/finalize`, {
        itr_v_acknowledgement: ackNumber.trim(),
        filed_on: new Date(filedOn).toISOString(),
        notes: noteText || undefined,
      });
      toast.success("Filing finalized — client notified.");
      setFinalizeOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const triggerDownload = async (relPath, filename) => {
    try {
      const resp = await api.get(relPath, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const status = filing.status;
  const isFiled = status === "filed_with_government";

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto">
        <BackButton to="/ca-portal/filings" label="Queue" />
        <PageHeader>
          <div>
            <PageHeader.Title eyebrow={`${filing.itr_type} · AY ${filing.assessment_year}`}>{client.full_name}</PageHeader.Title>
            <div className="text-sm text-muted-foreground mt-1">{client.email} · PAN <span className="font-mono uppercase">{(filing.data?.pan) || client.pan || "—"}</span></div>
            <Badge className="mt-2" variant="outline">{(STATUS_BADGES[status] || {}).label || status}</Badge>
          </div>
          <PageHeader.Actions>
            {!isFiled && (
              <>
                <Button disabled={busy || status === "ca_in_review"} variant={status === "ca_in_review" ? "default" : "outline"} onClick={() => setStatus("ca_in_review")}>Mark in review</Button>
                <Button disabled={busy} variant="outline" onClick={() => setStatus("ca_filing")}>Move to filing</Button>
                <Dialog open={finalizeOpen} onOpenChange={setFinalizeOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="finalize-btn" className="bg-accent hover:bg-accent/90 text-accent-foreground">
                      <ShieldCheck className="h-4 w-4 mr-2" /> Finalize with ITR-V
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Finalize filing</DialogTitle>
                      <DialogDescription>Enter the real ITR-V acknowledgement issued by incometax.gov.in. This is the only way to mark a return as Filed with the Income Tax Department.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs">ITR-V acknowledgement number (15 digits)</Label>
                        <Input value={ackNumber} onChange={(e) => setAckNumber(e.target.value)} className="h-11 mt-1.5 font-mono" placeholder="e.g. 123456789012345" data-testid="ack-input" />
                      </div>
                      <div>
                        <Label className="text-xs">Filed on</Label>
                        <Input type="date" value={filedOn} onChange={(e) => setFiledOn(e.target.value)} className="h-11 mt-1.5" />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setFinalizeOpen(false)}>Cancel</Button>
                      <Button onClick={finalize} disabled={busy} data-testid="confirm-finalize">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Finalize"}</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </PageHeader.Actions>
        </PageHeader>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          <div className="space-y-5">
            {/* Tax overview */}
            <Card className="p-6 border-border" data-testid="tax-overview">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Tax overview</div>
              <div className="mt-4 grid sm:grid-cols-3 gap-4 text-sm">
                <KV label="Gross income" v={inr(filing.computed_tax?.[filing.regime]?.gross_total_income)} />
                <KV label="Taxable income" v={inr(filing.computed_tax?.[filing.regime]?.taxable_income)} />
                <KV label="Total tax" v={inr(filing.computed_tax?.[filing.regime]?.total_tax)} />
                <KV label="Regime" v={filing.regime?.toUpperCase()} />
                <KV label="Std. deduction" v={inr(filing.computed_tax?.[filing.regime]?.standard_deduction)} />
                <KV label="Effective rate" v={`${filing.computed_tax?.[filing.regime]?.effective_rate || 0}%`} />
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => triggerDownload(`/filings/${filingId}/export/itr.json`, `itr-${filingId.slice(0,8)}.json`)}><Download className="h-3.5 w-3.5 mr-1.5" /> ITR JSON</Button>
                <Button size="sm" variant="outline" onClick={() => triggerDownload(`/filings/${filingId}/export/computation.pdf`, `computation-${filingId.slice(0,8)}.pdf`)}><Download className="h-3.5 w-3.5 mr-1.5" /> Computation</Button>
                <Button size="sm" variant="outline" onClick={() => triggerDownload(`/filings/${filingId}/export/summary.pdf`, `summary-${filingId.slice(0,8)}.pdf`)}><Download className="h-3.5 w-3.5 mr-1.5" /> Summary</Button>
                <Button size="sm" variant="outline" onClick={() => triggerDownload(`/filings/${filingId}/export/checklist.pdf`, `checklist-${filingId.slice(0,8)}.pdf`)}><Download className="h-3.5 w-3.5 mr-1.5" /> Checklist</Button>
              </div>
              {isFiled && (
                <StatusBanner variant="success" title="Filed with the Income Tax Department" className="mt-5">
                  ITR-V: <span className="font-mono">{filing.itr_v_acknowledgement}</span> · {new Date(filing.filed_on).toLocaleDateString()}
                </StatusBanner>
              )}
            </Card>

            {/* Chat */}
            <Card className="border-border flex flex-col h-[420px] overflow-hidden">
              <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-primary" />
                <div className="font-semibold text-sm">Client chat</div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.length === 0 && <div className="text-xs text-muted-foreground text-center pt-8">No messages yet.</div>}
                {messages.map(m => (
                  <div key={m.id} className={`flex ${m.from_role === "ca" || m.from_role === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.from_role === "ca" || m.from_role === "admin" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                      <div className="whitespace-pre-wrap">{m.text}</div>
                      <div className="mt-1 text-[10px] opacity-70">{new Date(m.at).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
              <form className="border-t border-border p-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); sendMsg(); }}>
                <Textarea value={msgText} onChange={(e) => setMsgText(e.target.value)} className="min-h-[40px] max-h-32 resize-none" placeholder="Message client…" />
                <Button type="submit" size="icon" disabled={!msgText.trim()}><Send className="h-4 w-4" /></Button>
              </form>
            </Card>

            {/* Internal notes */}
            <Card className="p-5 border-border">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Internal notes (not visible to client)</div>
              <div className="mt-3 space-y-2">
                {notes.length === 0 && <div className="text-xs text-muted-foreground">No notes.</div>}
                {notes.map(n => (
                  <div key={n.id} className="text-sm border-l-2 border-primary/30 pl-3 py-1">
                    <div>{n.text}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{new Date(n.at).toLocaleString()}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} className="min-h-[40px] max-h-32 resize-none" placeholder="Add a private note…" />
                <Button onClick={addNote} variant="outline">Add</Button>
              </div>
            </Card>
          </div>

          {/* SIDEBAR */}
          <div className="space-y-5">
            <Card className="p-5 border-border">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Request documents</div>
              <Input value={docName} onChange={(e) => setDocName(e.target.value)} placeholder="e.g. Form 16 Part B" className="h-10 mt-3" />
              <Textarea value={docReason} onChange={(e) => setDocReason(e.target.value)} placeholder="Reason / instructions" className="mt-2 min-h-[60px] resize-none" />
              <Button onClick={requestDoc} className="mt-2 w-full" variant="outline">Request from client</Button>
              <div className="mt-4 space-y-2">
                {docReqs.map(r => (
                  <div key={r.id} className="text-xs rounded border border-border p-2 flex items-center justify-between">
                    <div><div className="font-semibold">{r.doc_name}</div>{r.reason && <div className="text-muted-foreground">{r.reason}</div>}</div>
                    <Badge variant="outline">{r.status}</Badge>
                  </div>
                ))}
              </div>
            </Card>

            {checklist && (
              <Card className="p-5 border-border">
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Completion</div>
                  <div className="text-xs font-bold num-rupee">{checklist.progress}%</div>
                </div>
                <ul className="mt-3 space-y-1.5 text-xs">
                  {checklist.items.map(i => (
                    <li key={i.id} className="flex items-center gap-2">
                      <CheckCircle2 className={`h-3.5 w-3.5 ${i.done ? "text-accent" : "text-muted-foreground/40"}`} />
                      <span className={i.done ? "" : "text-muted-foreground"}>{i.label}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            <Card className="p-5 border-border">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold flex items-center gap-2"><Receipt className="h-3.5 w-3.5" /> Invoices</div>
              <div className="mt-3 space-y-2">
                {invoices.map(inv => (
                  <div key={inv.id} className="text-xs rounded border border-border p-2 flex justify-between">
                    <div><div className="font-mono">{inv.number}</div><div className="text-muted-foreground">{inv.description}</div></div>
                    <div className="num-rupee font-bold">{inr(inv.amount)}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 space-y-2">
                <div className="flex gap-2">
                  <Input type="number" value={invAmount} onChange={(e) => setInvAmount(e.target.value)} className="h-10" />
                  <Button onClick={createInvoice} variant="outline">Issue</Button>
                </div>
                <Input value={invDesc} onChange={(e) => setInvDesc(e.target.value)} className="h-10" />
              </div>
            </Card>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function KV({ label, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="mt-1 num-rupee font-display text-base font-semibold">{v}</div>
    </div>
  );
}
